import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Send, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { first_name: "", last_name: "", email: "", company: "", message: "" };

export const PricingRequestModal = ({ open, onClose, plan }) => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initial);
      setStatus("idle");
      setError("");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name || !form.last_name || !form.email || !form.message) {
      setError("Please fill in all required fields.");
      return;
    }
    setStatus("submitting");
    try {
      await axios.post(`${API}/contact`, {
        first_name: form.first_name,
        last_name: form.last_name,
        company: form.company,
        email: form.email,
        industry: "",
        message: form.message,
        plan: plan?.name || "",
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError("Couldn't send your request. Please try again or email hello@zelaflo.com.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
          style={{ backgroundColor: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
          onClick={onClose}
          data-testid="pricing-modal"
        >
          <motion.div
            initial={{ scale: 0.94, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 12, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="pricing-modal-card relative w-full max-w-lg rounded-2xl border p-6 md:p-8 max-h-[92vh] overflow-y-auto"
            data-testid="pricing-modal-card"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
              style={{ backgroundColor: "var(--card-tint)", color: "var(--text)" }}
              data-testid="pricing-modal-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            {status === "success" ? (
              <div className="text-center py-6" data-testid="pricing-modal-success">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.05 }}
                  className="w-16 h-16 mx-auto rounded-full bg-[#2DD4BF]/15 border border-[#2DD4BF]/40 flex items-center justify-center mb-5"
                >
                  <Check size={28} className="text-[#2DD4BF]" strokeWidth={3} />
                </motion.div>
                <h3 className="font-heading text-2xl font-semibold mb-2" style={{ color: "var(--text)" }}>
                  Request received!
                </h3>
                <p className="text-base" style={{ color: "var(--text-muted)" }}>
                  We'll reach out within 1 business day.
                </p>
                <button
                  onClick={onClose}
                  className="btn-outline-zl mt-7 px-6 py-2.5 rounded-full text-sm font-semibold"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6 pr-10">
                  <h3 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight" style={{ color: "var(--text)" }}>
                    Let's Get You Set Up
                  </h3>
                  {plan && (
                    <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }} data-testid="pricing-modal-plan-info">
                      You selected: <span className="font-semibold text-[#7B2FBE]">{plan.name}</span> — <span style={{ color: "var(--text)" }}>${plan.setup?.toLocaleString()} setup + ${plan.monthly}/mo</span>
                    </p>
                  )}
                </div>

                <form onSubmit={submit} className="space-y-4" data-testid="pricing-modal-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="First Name" required>
                      <input value={form.first_name} onChange={update("first_name")} required type="text" className={inputCls} data-testid="pm-input-first-name" />
                    </Field>
                    <Field label="Last Name" required>
                      <input value={form.last_name} onChange={update("last_name")} required type="text" className={inputCls} data-testid="pm-input-last-name" />
                    </Field>
                  </div>
                  <Field label="Work Email" required>
                    <input value={form.email} onChange={update("email")} required type="email" className={inputCls} data-testid="pm-input-email" />
                  </Field>
                  <Field label="Company Name">
                    <input value={form.company} onChange={update("company")} type="text" className={inputCls} data-testid="pm-input-company" />
                  </Field>
                  <Field label="Brief description of what you need automating" required>
                    <textarea value={form.message} onChange={update("message")} required rows={4} className={inputCls} data-testid="pm-input-message" />
                  </Field>

                  {error && (
                    <p className="text-sm text-red-400 inline-flex items-center gap-2" data-testid="pm-form-error">
                      <AlertCircle size={14} /> {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="btn-primary-zl w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold disabled:opacity-60"
                    data-testid="pm-submit-btn"
                  >
                    {status === "submitting" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    {status === "submitting" ? "Sending..." : "Send My Request"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const inputCls = "w-full rounded-xl px-4 py-3 transition-all duration-200 focus:outline-none focus:border-[#7B2FBE] pricing-modal-input";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-semibold mb-2 inline-block" style={{ color: "var(--text-subtle)" }}>
      {label} {required && <span className="text-[#7B2FBE]">*</span>}
    </span>
    {children}
  </label>
);

export default PricingRequestModal;
